import { colorFor, STATUS } from '../lib/definitions/palette';
import { connectionStateInfo, DECODE_HEALTH_COLOR, decodeHealthSeverity } from '../lib/definitions/health';
import { trustTierInfo } from '../lib/definitions/trust';
import { formatNumber, relativeTime } from "../lib/format";
import { useAppData } from "../state/AppData";
import type { MqttBroker } from "../api/types";

// Extracted from pages/MqttPage.tsx (2026-09-20) so the Health page can
// show the exact same broker connection/decode-health view rather than a
// second, drifting copy of the same logic — "reuse existing components
// over new ones" per the Health page brief.
export function MqttBrokerTable({ brokers }: { brokers: MqttBroker[] | null | "unavailable" }) {
  const { effectiveTheme } = useAppData();

  if (brokers === null) return <div className="node-detail-note">Loading…</div>;
  // "unavailable" (feature off, or genuinely unreachable) reads the same to
  // an operator as "none configured" — the app-wide backend-down banner
  // already covers a real outage, so this stays a plain empty state rather
  // than an alarming error line on a fresh install with no MQTT broker added.
  if (brokers === "unavailable" || brokers.length === 0) return <div className="empty-state">No MQTT brokers configured on this instance.</div>;

  return (
    <div className="node-list-table-wrap">
      <table className="node-list-table">
        <thead>
          <tr>
            <th>Broker</th>
            <th>Trust tier</th>
            <th>Connection</th>
            <th>Decoded / total</th>
            <th>Plaintext</th>
            <th>Undecryptable</th>
            <th>Last packet</th>
          </tr>
        </thead>
        <tbody>
          {brokers.map((b) => {
            const conn = connectionStateInfo(b.connection_state);
            const tier = trustTierInfo(b.trust_tier);
            const severity = decodeHealthSeverity(b.undecryptable_total, b.messages_total);
            return (
              <tr key={b.id}>
                <td>
                  {b.label} <span className="audit-subject-protocol">({b.id})</span>
                  {b.collision_suspected && (
                    <div className="mqtt-collision-note" title={b.collision_detail || undefined}>
                      <span className="badge" style={{ background: colorFor(STATUS.critical, effectiveTheme) }}>
                        Client ID collision suspected
                      </span>
                    </div>
                  )}
                </td>
                <td>
                  <span className="badge" style={{ background: colorFor(tier.color, effectiveTheme) }}>
                    {tier.short}
                  </span>
                </td>
                <td>
                  <span className="badge" style={{ background: colorFor(conn.color, effectiveTheme) }}>
                    {conn.label}
                  </span>
                  {b.reconnect_attempts > 0 && <span className="mqtt-muted-suffix"> · {b.reconnect_attempts} reconnect(s)</span>}
                  {b.last_error && <div className="mqtt-muted-suffix">{b.last_error}</div>}
                </td>
                <td>
                  <span className="badge" style={{ background: colorFor(DECODE_HEALTH_COLOR[severity], effectiveTheme) }}>
                    {formatNumber(b.decoded_total)} / {formatNumber(b.messages_total)}
                  </span>
                </td>
                <td>{formatNumber(b.plaintext_total)}</td>
                <td>{formatNumber(b.undecryptable_total)}</td>
                <td>{b.last_packet_at ? relativeTime(b.last_packet_at) : "never"}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
